import { useEffect, useState, useMemo } from 'react';
import { adminApi } from '../../lib/api.js';
import { useToast } from '../../hooks/useToast.jsx';
import DataTable from '../../components/ui/DataTable.jsx';
import Button from '../../components/ui/Button.jsx';
import Input from '../../components/ui/Input.jsx';
import Select from '../../components/ui/Select.jsx';
import Badge from '../../components/ui/Badge.jsx';
import { Card, CardContent } from '../../components/ui/Card.jsx';

const REPORT_TYPES = [
  { value: 'summary', label: 'Leave Summary' },
  { value: 'requests', label: 'Request History' },
  { value: 'approvals', label: 'Approval Metrics' },
  { value: 'alerts', label: 'Balance Alerts' },
];

const STATUS_VARIANT = { pending: 'warning', approved: 'success', rejected: 'danger', cancelled: 'default' };
const currentYear = String(new Date().getFullYear());

function downloadCSV(filename, headers, rows) {
  const escape = (v) => {
    const s = String(v ?? '');
    return s.includes(',') || s.includes('"') || s.includes('\n') ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(escape).join(',')];
  for (const row of rows) lines.push(row.map(escape).join(','));
  const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function ReportsPage() {
  const toast = useToast();
  const [reportType, setReportType] = useState('summary');
  const [year, setYear] = useState(currentYear);
  const [department, setDepartment] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  async function load() {
    setLoading(true);
    try {
      const res = await adminApi.getReport(reportType, year, department);
      setData(res);
    } catch (err) {
      toast.error(`Failed to load report: ${err.message}`);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
  }, [reportType, year, department]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold tracking-tight">Reports</h1>
        <p className="text-muted-foreground">Leave data analytics and exports</p>
      </div>

      <div className="mb-6 flex flex-wrap items-end gap-3">
        <Select
          label="Report type"
          value={reportType}
          onChange={(e) => { setReportType(e.target.value); setData(null); }}
          options={REPORT_TYPES}
        />
        <Select
          label="Year"
          value={year}
          onChange={(e) => setYear(e.target.value)}
          options={[
            { value: '2025', label: '2025' },
            { value: '2026', label: '2026' },
            { value: '2027', label: '2027' },
          ]}
        />
        <Input
          label="Department"
          value={department}
          onChange={(e) => setDepartment(e.target.value)}
          placeholder="All departments"
          className="max-w-[200px]"
        />
      </div>

      {loading ? (
        <div className="flex items-center justify-center p-8 text-muted-foreground">
          <div className="h-5 w-5 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          <span className="ml-3 text-sm">Loading report...</span>
        </div>
      ) : data ? (
        <>
          {reportType === 'summary' && <SummaryReport data={data} year={year} />}
          {reportType === 'requests' && <RequestsReport data={data} />}
          {reportType === 'approvals' && <ApprovalsReport data={data} />}
          {reportType === 'alerts' && <AlertsReport data={data} />}
        </>
      ) : null}
    </div>
  );
}

function SummaryReport({ data, year }) {
  if (!data.rows || data.rows.length === 0) {
    return <EmptyState message="No balance data found for this period." />;
  }

  const typeKeys = Object.keys(data.rows[0]?.balances || {});

  function handleExport() {
    const headers = ['Name', 'Email', 'Department'];
    for (const tk of typeKeys) {
      const name = data.rows[0].balances[tk]?.typeName || tk;
      headers.push(`${name} Allowance`, `${name} Used`, `${name} Pending`, `${name} Remaining`);
    }
    const rows = data.rows.map((r) => {
      const row = [r.displayName, r.email, r.department];
      for (const tk of typeKeys) {
        const b = r.balances[tk] || {};
        row.push(b.allowance, b.used, b.pending, b.remaining);
      }
      return row;
    });
    downloadCSV(`leave-summary-${year}.csv`, headers, rows);
  }

  const columns = [
    { key: 'displayName', label: 'Employee', render: (r) => (
      <div>
        <div className="font-medium">{r.displayName}</div>
        <div className="text-xs text-muted-foreground">{r.department || '\u2014'}</div>
      </div>
    )},
    ...typeKeys.map((tk) => ({
      key: tk,
      label: data.rows[0]?.balances[tk]?.typeName || tk,
      render: (r) => {
        const b = r.balances[tk] || {};
        const colorClass = b.remaining < 0 ? 'text-profun-pink' : b.remaining <= 3 ? 'text-amber-600' : 'text-emerald-600';
        return (
          <div className="text-center">
            <span className={`font-semibold ${colorClass}`}>{b.remaining ?? 0}</span>
            <span className="text-[11px] text-muted-foreground"> / {(b.allowance || 0) + (b.carriedOver || 0)}</span>
          </div>
        );
      },
    })),
  ];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <span className="text-sm text-muted-foreground">{data.rows.length} employees</span>
        <Button variant="secondary" size="sm" onClick={handleExport}>Export CSV</Button>
      </div>
      <DataTable columns={columns} rows={data.rows} loading={false} keyFn={(r) => r.userId} />
    </div>
  );
}

function RequestsReport({ data }) {
  if (!data.rows || data.rows.length === 0) {
    return <EmptyState message="No requests found for this period." />;
  }

  function handleExport() {
    const headers = ['Employee', 'Department', 'Type', 'Start', 'End', 'Days', 'Status', 'Submitted'];
    const rows = data.rows.map((r) => [r.requesterName, r.department, r.typeName, r.startDate, r.endDate, r.days, r.status, r.submittedAt]);
    downloadCSV(`leave-requests-${data.year}.csv`, headers, rows);
  }

  const columns = [
    { key: 'requesterName', label: 'Employee' },
    { key: 'typeName', label: 'Type' },
    { key: 'startDate', label: 'Start' },
    { key: 'endDate', label: 'End' },
    { key: 'days', label: 'Days' },
    { key: 'status', label: 'Status', render: (r) => <Badge variant={STATUS_VARIANT[r.status] || 'default'}>{r.status}</Badge> },
  ];

  return (
    <div>
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-4 text-sm text-muted-foreground">
          <span>{data.total} total</span>
          {Object.entries(data.byStatus || {}).map(([s, c]) => (
            <span key={s} className="flex items-center gap-1"><Badge variant={STATUS_VARIANT[s] || 'default'}>{s}</Badge> {c}</span>
          ))}
        </div>
        <Button variant="secondary" size="sm" onClick={handleExport}>Export CSV</Button>
      </div>
      <DataTable columns={columns} rows={data.rows} loading={false} keyFn={(r) => r.requestId} />
    </div>
  );
}

function ApprovalsReport({ data }) {
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
      <MetricCard label="Total Requests" value={data.total} />
      <MetricCard label="Approved" value={data.approvedCount} className="text-emerald-600" />
      <MetricCard label="Rejected" value={data.rejectedCount} className="text-profun-pink" />
      <MetricCard label="Pending" value={data.pendingCount} className="text-amber-600" />
      <MetricCard label="Cancelled" value={data.cancelledCount} />
      <MetricCard label="Approval Rate" value={`${data.approvalRate}%`} className="text-primary" />
      <MetricCard label="Avg Approval Time" value={`${data.avgApprovalHours}h`} className="text-primary" />
    </div>
  );
}

function AlertsReport({ data }) {
  if (!data.alerts || data.alerts.length === 0) {
    return <EmptyState message="No balance alerts. Everyone is within healthy limits." />;
  }

  const columns = [
    { key: 'displayName', label: 'Employee', render: (r) => (
      <div>
        <div className="font-medium">{r.displayName}</div>
        <div className="text-xs text-muted-foreground">{r.department || '\u2014'}</div>
      </div>
    )},
    { key: 'typeName', label: 'Leave Type' },
    { key: 'remaining', label: 'Remaining', render: (r) => (
      <span className={`font-semibold ${r.severity === 'critical' ? 'text-profun-pink' : 'text-amber-600'}`}>
        {r.remaining} days
      </span>
    )},
    { key: 'allowance', label: 'Of Total', render: (r) => `${r.allowance} days` },
    { key: 'severity', label: 'Severity', render: (r) => (
      <Badge variant={r.severity === 'critical' ? 'danger' : 'warning'}>{r.severity}</Badge>
    )},
  ];

  return (
    <div>
      <p className="mb-4 text-sm text-muted-foreground">
        {data.count} alert{data.count !== 1 ? 's' : ''} — employees with less than 20% balance remaining or negative balance
      </p>
      <DataTable columns={columns} rows={data.alerts} loading={false} keyFn={(r) => `${r.userId}_${r.typeId}`} />
    </div>
  );
}

function MetricCard({ label, value, className }) {
  return (
    <Card>
      <CardContent className="p-5">
        <div className="mb-2 text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</div>
        <div className={`text-2xl font-bold ${className || 'text-foreground'}`}>{value}</div>
      </CardContent>
    </Card>
  );
}

function EmptyState({ message }) {
  return (
    <div className="rounded-lg border border-dashed border-border p-12 text-center text-sm text-muted-foreground">
      {message}
    </div>
  );
}
